/* Customer-Trader visibility predicate — the ONE frontend copy.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * ``is_customer_trader`` is a visibility RESTRICTION, and every backend
 * enforcement point exempts superusers (see
 * ``fuel_enquiries.serializers._request_is_customer_trader`` and the twin in
 * ``offer.serializers``, both of which return False for a superuser). The
 * frontend did not have one shared copy of that rule, so the gates drifted:
 *
 *   - HMSNavbar got the superuser exemption on 2026-08-16 (commit 237d211,
 *     inside an unrelated Teams change, with no comment).
 *   - FuelEnquiryForm kept the original 2026-07-06 form of the check
 *     (commit 2a2fa8b) — the trader flag ALONE — and was never back-fixed.
 *
 * Live consequence (prod, found 2026-08-18): the Head of Bunkers is a
 * superuser whose account ALSO carries ``is_customer_trader`` (set during
 * testing). The API sent her the full enquiry payload — supplier pricing,
 * 3rd-party costs, the paired purchase recap — because the backend exempts
 * superusers, and the React form then refused to render the Supplier Pricing
 * and 3rd Party Costs sections because its gate keyed on the flag alone.
 * The data crossed the API seam and died in the render.
 *
 * PREMISE: a superuser is never a restricted customer trader. This mirrors the
 * backend exactly. If a backend overlay ever STOPS exempting superusers, this
 * module must change with it or the two sides will disagree again.
 *
 * Every frontend customer-trader gate must call ``isRestrictedCustomerTrader``
 * — never read ``is_customer_trader`` directly. The raw flag is the input to
 * this rule, not the rule.
 */

/**
 * True when the user is a customer trader whose visibility is actually
 * restricted — i.e. flagged AND not a superuser.
 *
 * Accepts any object carrying the flags (the ``/user-info`` payload, the
 * ``currentUser`` state in a form, a raw employee dict). Missing / null users
 * fail CLOSED to ``false``: an unresolved user is not a restricted trader, so
 * a gate keyed on this never hides a section from an admin while their
 * profile is still loading. Row-level scoping is enforced server-side
 * regardless, so this is a UI-affordance rule, not a security boundary.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const isRestrictedCustomerTrader = (user) => (
  Boolean(user && user.is_customer_trader && !user.is_superuser)
);

export default isRestrictedCustomerTrader;
